/**
 * Баннеры «в парке» — поведение и пресеты как на Солнышко, но цель клика —
 * страница ГОРОДА баннера /{slug}?<пресеты> (а не /{locale}/rent). Карточки
 * вставляются в сетку авто по placement_mode (random/fixed/every). Клик шлёт
 * /banner/hit и ставит cookie ap_banner для атрибуции брони.
 */

import type { RentBannerWithCity } from "./fleet-server";
import { promoToken } from "./promoToken";

// Путь картинки баннера → URL. На autopilotrent rent-site картинки лежат под
// /images/... (как city.image_url) и отдаются нативно — путь берём КАК ЕСТЬ
// (в админке тот же файл проксируется как /rent-site-img/ → autopilotrent/images/).
export function bannerImageSrc(url?: string): string {
  return (url || "").trim();
}

// Тип баннера: promo (/promo), park (/rent — поиск с пресетом), link (прочее).
export function bannerKind(b: { page?: string }): "promo" | "park" | "link" {
  const page = (b.page || "/rent").trim();
  if (page === "/promo") return "promo";
  if (page === "/rent") return "park";
  return "link";
}

export function bannerKey(b: RentBannerWithCity): string {
  return b.id || `${b.station}:${b.title_ru || b.image || ""}`;
}
export function bannerLabel(b: RentBannerWithCity): string {
  return (b.title_ru || b.title_en || "").trim();
}

// URL клика. external → как есть; прочая внутренняя страница (не /rent,/promo) →
// как есть; park/promo → страница ГОРОДА баннера с пресетами (их читает SearchBar).
export function buildBannerLink(b: RentBannerWithCity): string {
  const page = (b.page || "/rent").trim();
  if (/^https?:\/\//i.test(page)) return page;
  // Промо → токен-страница /promo города (даты добавит BannerCard из поиска).
  if (page === "/promo") {
    const pp = new URLSearchParams({ city: b.slug, k: promoToken(b.slug) });
    return `/promo?${pp.toString()}`;
  }
  if (page !== "/rent") return page;
  const p = new URLSearchParams();
  const f = b.filter || {};
  if (f.car_class) p.set("class", f.car_class);
  if (f.brand) p.set("brand", f.brand);
  const models = (f.car_models || []).filter((m) => m && m.name);
  if (models.length) p.set("models", models.map((m) => `${m.name}__${m.year ?? ""}`).join("|"));
  if (f.delivery_place_guid) p.set("delivery", f.delivery_place_guid);
  if (f.min_days) p.set("min_days", String(f.min_days));
  if (f.start_offset_days !== undefined && f.start_offset_days !== null) p.set("start_offset", String(f.start_offset_days));
  if (f.rental_days) p.set("rental_days", String(f.rental_days));
  if (b.booking_preset?.region_guid) p.set("region", b.booking_preset.region_guid);
  if (b.booking_preset?.option_guid) p.set("option", b.booking_preset.option_guid);
  const q = p.toString();
  return `/${b.slug}${q ? `?${q}` : ""}`;
}

// Клик: фиксируем переход (все баннеры) + cookie атрибуции (park/promo).
// Изолировано — ошибки не мешают навигации.
export function trackBannerClick(b: RentBannerWithCity): void {
  try {
    const id = bannerKey(b);
    const kind = bannerKind(b);
    const title = bannerLabel(b);
    const payload = JSON.stringify({ station: b.station, banner_id: id, title, kind, site: "autopilot" });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon("/api/v1/banner/hit", new Blob([payload], { type: "application/json" }));
    }
    if ((kind === "park" || kind === "promo") && typeof document !== "undefined") {
      const c = encodeURIComponent(JSON.stringify({ id, title, kind, station: b.station, site: "autopilot" }));
      document.cookie = `ap_banner=${c}; path=/; max-age=${7 * 24 * 3600}; samesite=lax`;
    }
  } catch {
    /* трекинг не должен ломать переход */
  }
}

// Читает атрибуцию из cookie (на странице брони) — для проброса в state.
export function readBannerAttribution(): { id: string; title: string; kind: string } | null {
  try {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(/(?:^|;\s*)ap_banner=([^;]+)/);
    if (!m) return null;
    const o = JSON.parse(decodeURIComponent(m[1])) as { id?: string; title?: string; kind?: string };
    return o?.id ? { id: o.id, title: o.title || "", kind: o.kind || "" } : null;
  } catch {
    return null;
  }
}

// Вставка баннеров в список карточек авто по placement_mode (детерминированно —
// SSR/клиент совпадают). every N — после каждой N-й карточки; fixed N — на
// позицию N; random — позиция из сид-хэша заголовка+картинки.
export type GridSlot<T> = { kind: "car"; car: T } | { kind: "banner"; banner: RentBannerWithCity; key: string };

export function interleaveBanners<T>(cars: T[], banners: RentBannerWithCity[]): GridSlot<T>[] {
  let mixed: GridSlot<T>[] = cars.map((c) => ({ kind: "car", car: c }));

  banners.filter((b) => (b.placement_mode || "random") === "every" && (b.placement_value || 0) >= 1).forEach((b, bi) => {
    const out: GridSlot<T>[] = [];
    let carCount = 0;
    for (const it of mixed) {
      out.push(it);
      if (it.kind === "car") {
        carCount++;
        if (carCount % (b.placement_value as number) === 0) out.push({ kind: "banner", banner: b, key: `ev-${bi}-${carCount}` });
      }
    }
    mixed = out;
  });

  banners.filter((b) => b.placement_mode === "fixed" && (b.placement_value || 0) >= 1).forEach((b, bi) => {
    const pos = Math.min(b.placement_value as number, mixed.length);
    mixed.splice(pos, 0, { kind: "banner", banner: b, key: `fx-${bi}` });
  });

  banners.filter((b) => (b.placement_mode || "random") === "random").forEach((b, bi) => {
    const seed = ((b.title_ru || "") + (b.image || "")).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    const pos = mixed.length > 0 ? seed % (mixed.length + 1) : 0;
    mixed.splice(pos, 0, { kind: "banner", banner: b, key: `rn-${bi}` });
  });

  return mixed;
}
